import type { FC } from "react";
import { Fragment } from "react";
import { createPortal } from "react-dom";

import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { GiftIcon } from "@jti/brand-icons/gift";
import { Button } from "@jti/core-components/components/Button";
import { isServerSide } from "@jti/fe-common";
import type { BuyPrizeElementResponseType } from "@jti/rest-api-types";
import { CopyCodeButton } from "@jti/ui/components/CopyCodeButton";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ActionButtons } from "../../ActionButtons";
import { Line } from "../../Line";

import styles from "./VirtualPrizeConfirmation.module.scss";

const { sanitize } = dompurify;

export type VirtualPrizeConfirmationProps = {
  rewardTitle: string;
  onComplete?: () => void;
  onExploreMoreRewards?: () => void;
  prizesData?: BuyPrizeElementResponseType;
};

export const VirtualPrizeConfirmation: FC<VirtualPrizeConfirmationProps> = ({
  rewardTitle,
  onExploreMoreRewards,
  onComplete,
  prizesData,
}) => {
  const { __ } = useTranslations();
  const { breakpoint } = useDeviceBreakpoint();
  const isSmallScreen = breakpoint === "mobile";
  const { LOYALTY_PROFILE_PAGE_URL, TERMS_PAGE_URL } = useConfig();
  const { codes = [], codes_type, vouchers } = prizesData ?? {};

  const formatWithLineBreaks = (text: string) => text.replace(/\n/g, "<br /><br />");

  const resolveRedemptionInstructions = (): string => {
    const voucherRedemptionInstructions = (vouchers ?? []).find((v) =>
      Boolean(v.redemption_instructions)
    )?.redemption_instructions;
    if (voucherRedemptionInstructions) {
      const translationKey = voucherRedemptionInstructions.trim();
      if (translationKey.startsWith("loyalty.")) {
        return __(translationKey);
      }
      return voucherRedemptionInstructions;
    }
    return codes_type === "coupon"
      ? __("loyalty.redeemDialog.howToUseDescription.coupon")
      : __("loyalty.redeemDialog.howToUseDescription.url");
  };

  return (
    <>
      <div className={styles.prizeNameWrapper}>
        <h4 className={styles.prizeName}>{rewardTitle}</h4>
        <div
          className={styles.pointsHistoryDescription}
          dangerouslySetInnerHTML={{
            __html: sanitize(__("loyalty.redeemDialog.pointsHistoryDescription", [LOYALTY_PROFILE_PAGE_URL]), {
              ADD_ATTR: ["target"],
            }),
          }}
        />
      </div>
      {codes_type === "url" &&
        (vouchers?.length && codes_type === "url" ? vouchers.map((v) => v.code) : codes).map((code) => (
          <Button
            key={code}
            size={isSmallScreen ? "sm" : "md"}
            variant="secondary"
            target="_blank"
            href={code}
            className={styles.claimYourRewardButton}
            iconAfter={GiftIcon}
          >
            {__("loyalty.redeemDialog.claimYourRewardButtonText")}
          </Button>
        ))}

      {codes_type === "coupon" &&
        (vouchers?.length && codes_type === "coupon" ? vouchers.map((v) => v.code) : codes).map((code) => (
          <div className={styles.couponCodeWrapper} key={code}>
            <CopyCodeButton value={code ?? ""} key={code} />
          </div>
        ))}

      {codes_type === "url_with_pin" &&
        (vouchers ?? []).map(({ code, pin }) =>
          pin ? (
            <Fragment key={pin}>
              <div className={styles.couponCodeWrapper}>
                <CopyCodeButton value={pin} prefix="PIN:" />
              </div>
              <Button
                size={isSmallScreen ? "sm" : "md"}
                variant="secondary"
                target="_blank"
                href={code}
                className={styles.claimYourRewardButton}
                iconAfter={GiftIcon}
              >
                {__("loyalty.redeemDialog.claimYourRewardButtonText")}
              </Button>
            </Fragment>
          ) : null
        )}
      <div className={styles.textSectionWrapper}>
        <h5 className={styles.textSectionLabel}>{`${__("loyalty.redeemDialog.howToUseLabel")}: ${rewardTitle}`}</h5>
        <div
          className={styles.howToUseDescription}
          dangerouslySetInnerHTML={{
            __html: sanitize(formatWithLineBreaks(resolveRedemptionInstructions()), {
              ADD_ATTR: ["target"],
              FORBID_ATTR: ["class", "style"],
            }),
          }}
        />
      </div>
      <Line />
      <div className={styles.textSectionWrapper}>
        <h5 className={styles.textSectionLabel}>{__("loyalty.redeemDialog.nextStepsLabel")}</h5>
        <p className={styles.textSectionDescription}>{__("loyalty.redeemDialog.simplified.nextStepsDescription")}</p>
      </div>

      {!isSmallScreen && (
        <>
          <ActionButtons
            primaryButtonLabel={__("loyalty.redeemDialog.confirmButtonText")}
            secondaryButtonLabel={__("loyalty.redeemDialog.exploreMoreRewardsButtonText")}
            onPrimaryButtonClick={onComplete}
            onSecondaryButtonClick={onExploreMoreRewards}
          />
          <div
            className={styles.termsAndConditions}
            dangerouslySetInnerHTML={{
              __html: sanitize(__(`loyalty.redeemDialog.termsAndConditionsText`, [TERMS_PAGE_URL]), {
                ADD_ATTR: ["target"],
              }),
            }}
          />
        </>
      )}

      {isSmallScreen &&
        !isServerSide() &&
        createPortal(
          <div className={styles.actionButtonsWrapper}>
            <ActionButtons
              primaryButtonLabel={__("loyalty.redeemDialog.confirmButtonText")}
              secondaryButtonLabel={__("loyalty.redeemDialog.exploreMoreRewardsButtonText")}
              onPrimaryButtonClick={onComplete}
              onSecondaryButtonClick={onExploreMoreRewards}
            />
            <div
              className={styles.termsAndConditions}
              dangerouslySetInnerHTML={{
                __html: sanitize(__(`loyalty.redeemDialog.termsAndConditionsText`, [TERMS_PAGE_URL]), {
                  ADD_ATTR: ["target"],
                }),
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
};
